/**
 * The violin: a touch now and then rather than a part all the way through. It plays one short figure
 * at the end of a phrase, where the singer breathes, and leans into the chord that comes next.
 *
 * - An answer is a run of eighths up or down into the next chord's third.
 * - A swell holds a note of the chord across the bar, growing, and lands on that third.
 * - A turn wraps that third: the note above it, the third, the note below, then the landing.
 * - A fall leaps up to a high note of the chord, holds it a beat, and steps down into the third.
 * - A climb leaps up the chord's own tones in eighths into the third; a cascade comes down them.
 * - A rise is the fall turned over: a low note of the chord held a beat, two steps up into the third.
 * - A circle sounds the third early and holds it, then the note above it and below it, and lands.
 *
 * The eight take turns, and a run carries the turn on from part to part (`variation`), so a song
 * plays all eight before it plays one again.
 *
 * The landing dies away rather than stopping, the way a bow lightens off the string. Its swell and
 * fade are gain changes the notes carry in `levels`, which the player ramps the violin's gain to.
 *
 * A leaf, like chordPad.js: it reads chord symbols and hands back MIDI numbers.
 */

import { STRONG } from './chartIntensity';
import { getChordNotes } from './chordNotes';
import { getPitchClass } from './chordVoicing';

export const ANSWER = 'answer';
export const SWELL = 'swell';
export const TURN = 'turn';
export const FALL = 'fall';
export const CLIMB = 'climb';
export const RISE = 'rise';
export const CASCADE = 'cascade';
export const CIRCLE = 'circle';
/** The order the figures take turns in: a busy one, then a held one. */
export const FIGURES = [ANSWER, SWELL, TURN, FALL, CLIMB, RISE, CASCADE, CIRCLE];
/** A level that ramps in dB rather than in gain, for a fade heard as even all the way down. */
export const EXPONENTIAL = 'exponential';

// A phrase is four bars. Below Strong the violin waits for every other one
const PHRASE_BARS = 4;
const QUIET_PHRASES = 2;
// Where the figures sit: E4 to D6, around D5, above the piano's hands and the pad
const LOW = 64;
const HIGH = 86;
const CENTRE = 74;
const RUN_NOTES = 4;
// The chord's tones are a leap apart, so a climb or cascade takes three of them, not four
const ARPEGGIO_NOTES = 3;
// A fall's high note sits about a sixth over the landing, and at least a fourth
const FALL_LEAP = 9;
const FALL_MIN = 5;
// A run note is let go a little short of the next, so the bow is heard changing
const RUN_SHARE = 0.9;
// How hard each part of a figure plays, against the layer's own velocity
const RUN_FROM = 0.6;
const RUN_RISE = 0.05;
const HELD = 0.8;
const LANDING = 0.85;
// The swell grows from this gain to full across its held note
const SWELL_FROM = 0.4;
// The landing holds full for half its beats, then fades to -40 dB over the rest of it and this tail
const TAIL = 2;
const FADE_TO = 0.01;
const EPSILON = 1e-6;

// The notes a run walks over a chord: its pentatonic, which stays in the key on any degree of a
// major key, or only its own tones where it has no plain third and fifth (dim, sus, aug)
const runClasses = (chord) => {
  const { root, intervals } = getChordNotes(chord);
  const rootClass = getPitchClass(root);

  if (rootClass === null) return [];

  const has = (interval) => intervals.includes(interval);
  const steps =
    has(4) && has(7) ? [0, 2, 4, 7, 9] : has(3) && has(7) ? [0, 3, 5, 7, 10] : intervals.map((step) => step % 12);

  return [...new Set(steps.map((step) => (rootClass + step) % 12))];
};

const toneClasses = (chord) => {
  const { root, intervals } = getChordNotes(chord);
  const rootClass = getPitchClass(root);

  return rootClass === null ? [] : intervals.map((step) => (rootClass + step) % 12);
};

const pitchesOf = (classes) =>
  Array.from({ length: HIGH - LOW + 1 }, (_, i) => LOW + i).filter((midi) => classes.includes(midi % 12));

const nearest = (pitches, to) =>
  pitches.reduce((best, midi) => (best === null || Math.abs(midi - to) < Math.abs(best - to) ? midi : best), null);

// Where a figure lands: the chord's third, else its fifth, else its root, near the middle of the range
const landingPitch = (chord) => {
  const { root, intervals } = getChordNotes(chord);
  const rootClass = getPitchClass(root);
  const interval = [4, 3, 7, 0].find((wanted) => intervals.includes(wanted));

  if (rootClass === null || interval === undefined) return null;

  return nearest(pitchesOf([(rootClass + interval) % 12]), CENTRE);
};

// Where a landed note has to stop: the first chord after it that doesn't have it, a melody line, or a
// rest. A strike's `end` is read where it has one, for the next part's at a tempo of its own.
const ringsUntil = (strikes, time, pitch, secondsPerBeat) => {
  let reached = time;

  for (const strike of strikes) {
    const strikeEnd = strike.end ?? strike.time + strike.beats * secondsPerBeat;

    if (strikeEnd > time + EPSILON) {
      if (strike.time > reached + EPSILON) return reached;
      if (strike.time > time + EPSILON && (strike.melody || !toneClasses(strike.chord).includes(pitch % 12))) {
        return strike.time;
      }

      reached = strikeEnd;
    }
  }

  return reached;
};

// A repeatable coin, so the same chart plays its runs the same way every time
const coin = (bar, pitch) => {
  const x = Math.sin((bar * 31.7 + pitch) * 12.9898) * 43758.5453;

  return x - Math.floor(x) < 0.5;
};

/**
 * A run, walked back from where it lands: each note the next one along from the note after it, over
 * the chord under it. Down comes in from above. Null where the range runs out.
 */
const walkInto = (target, down, chordAt, { to, unit, count = RUN_NOTES, over = runClasses }) => {
  const run = [];
  let after = target;

  for (let k = 1; k <= count; k += 1) {
    const time = to - k * unit;
    const pitches = pitchesOf(over(chordAt(time)));
    const from = after;
    const midi = down ? pitches.find((pitch) => pitch > from) : pitches.filter((pitch) => pitch < from).pop();

    if (midi === undefined) return null;

    run.unshift({ time, midi });
    after = midi;
  }

  return run;
};

// The notes either side of a pitch on the scale under it
const above = (chord, midi) => pitchesOf(runClasses(chord)).find((pitch) => pitch > midi);
const below = (chord, midi) =>
  pitchesOf(runClasses(chord))
    .filter((pitch) => pitch < midi)
    .pop();

/**
 * What leads into the landing, each note `{ time, midi, hold?, level? }`, or null where the figure
 * doesn't fit the range. A swell isn't one: it ties into its landing.
 */
const leadIns = {
  [ANSWER]: (target, { to, unit, chordAt, down }) =>
    walkInto(target, down, chordAt, { to, unit }) || walkInto(target, !down, chordAt, { to, unit }),
  [TURN]: (target, { to, unit, chordAt }) => {
    const upper = above(chordAt(to - 3 * unit), target);
    const lower = below(chordAt(to - unit), target);

    if (upper === undefined || lower === undefined) return null;

    return [
      { time: to - 3 * unit, midi: upper },
      { time: to - 2 * unit, midi: target },
      { time: to - unit, midi: lower },
    ];
  },
  [FALL]: (target, { to, unit, chordAt }) => {
    const steps = walkInto(target, true, chordAt, { to, unit, count: 2 });
    const time = to - 4 * unit;

    if (!steps) return null;

    const high = nearest(
      pitchesOf(toneClasses(chordAt(time))).filter((pitch) => pitch >= Math.max(target + FALL_MIN, steps[0].midi + 2)),
      target + FALL_LEAP,
    );

    return high === null ? null : [{ time, midi: high, hold: 2 * unit, level: HELD }, ...steps];
  },
  [RISE]: (target, { to, unit, chordAt }) => {
    const steps = walkInto(target, false, chordAt, { to, unit, count: 2 });
    const time = to - 4 * unit;

    if (!steps) return null;

    const low = nearest(
      pitchesOf(toneClasses(chordAt(time))).filter((pitch) => pitch <= Math.min(target - FALL_MIN, steps[0].midi - 2)),
      target - FALL_LEAP,
    );

    return low === null ? null : [{ time, midi: low, hold: 2 * unit, level: HELD }, ...steps];
  },
  // Up the chord's tones, or down them where the range runs out
  [CLIMB]: (target, { to, unit, chordAt }) =>
    walkInto(target, false, chordAt, { to, unit, count: ARPEGGIO_NOTES, over: toneClasses }) ||
    walkInto(target, true, chordAt, { to, unit, count: ARPEGGIO_NOTES, over: toneClasses }),
  // Down the chord's tones. Near the top of the range, two of them, the first held a quarter.
  [CASCADE]: (target, { to, unit, chordAt }) => {
    const three = walkInto(target, true, chordAt, { to, unit, count: ARPEGGIO_NOTES, over: toneClasses });
    const two = !three && walkInto(target, true, chordAt, { to, unit, count: 2, over: toneClasses });

    return three || (two && [{ ...two[0], time: to - 3 * unit, hold: 2 * unit }, two[1]]);
  },
  [CIRCLE]: (target, { to, unit, chordAt }) => {
    const upper = above(chordAt(to - 2 * unit), target);
    const lower = below(chordAt(to - unit), target);

    if (upper === undefined || lower === undefined) return null;

    return [
      { time: to - 4 * unit, midi: target, hold: 2 * unit, level: HELD },
      { time: to - 2 * unit, midi: upper },
      { time: to - unit, midi: lower },
    ];
  },
};

/**
 * The bars a figure goes on: the last of each phrase, or of every other one below Strong, and the
 * chart's last bar where it has more than one.
 *
 * @returns {number[]} 0-based bar indexes
 */
export const getViolinBars = (bars, intensity) => {
  const every = PHRASE_BARS * (intensity >= STRONG ? 1 : QUIET_PHRASES);
  const ends = Array.from({ length: bars }, (_, bar) => bar).filter((bar) => (bar + 1) % every === 0);

  return bars > 1 && !ends.includes(bars - 1) ? [...ends, bars - 1] : ends;
};

/**
 * The violin's notes under a run of strikes.
 *
 * A figure needs a chord sounding at the end of its bar and one coming in on the bar line after it,
 * so a bar with a melody line in it, a rest at its end, or nothing after it is passed over.
 *
 * @param {object[]} strikes - From getChordStrikes
 * @param {object} options - `{ bars, beatsPerBar, secondsPerBeat, velocity, intensity, then, rises, following,
 *   variation }`. `variation` is where in FIGURES the chart's first figure starts: how many figures
 *   the run has played before it, so the turn carries on from part to part.
 *   `then` is the chord after the chart, `rises` whether the part after it plays louder, which the
 *   last bar swells into, and `following` that part's strikes, with their `end`s, timed from its start.
 *   Without them a landing past the chart rings only its own beats.
 * @returns {Array<{midi: number, time: number, duration: number, velocity: number, levels?: object[]}>}
 *   `levels` are `{ time, value, ramp, curve? }` gain changes, in the clip's time, for the player to ramp to
 */
export const getViolinNotes = (
  strikes,
  { bars, beatsPerBar, secondsPerBeat, velocity, intensity, then = null, rises = false, following = [], variation = 0 },
) => {
  const secondsPerBar = beatsPerBar * secondsPerBeat;
  // Eighths: a beat in 6/8, half of one in 3/4 and 4/4
  const unit = beatsPerBar % 3 === 0 && beatsPerBar > 3 ? secondsPerBeat : secondsPerBeat / 2;
  const landBeats = beatsPerBar % 3 === 0 && beatsPerBar > 3 ? 3 : 2;
  const end = bars * secondsPerBar;
  const sounding = (time) =>
    strikes.find(
      (strike) => strike.time <= time + EPSILON && strike.time + strike.beats * secondsPerBeat > time + EPSILON,
    );
  const notes = [];
  const after = following.map((strike) => ({ ...strike, time: strike.time + end, end: strike.end + end }));

  const figureBars = getViolinBars(bars, intensity);

  figureBars.forEach((bar, index) => {
    const from = bar * secondsPerBar;
    const to = from + secondsPerBar;
    const current = sounding(to - unit / 2);
    const next = to >= end - EPSILON ? (then ? { chord: then } : null) : sounding(to);
    const inBar = strikes.some(
      (strike) => strike.melody && strike.time >= from - EPSILON && strike.time < to - EPSILON,
    );

    if (!current || current.melody || !next || next.melody || inBar) return;

    const target = landingPitch(next.chord);

    if (target === null) return;

    // Rings on while the chords keep its note, never past the next figure. Past the chart, on into the
    // part after it, or only its own beats where those chords aren't known.
    const hold = landBeats * secondsPerBeat;
    const nextFigure = figureBars[index + 1] !== undefined ? figureBars[index + 1] * secondsPerBar : Infinity;
    const ringsTo =
      next.time === undefined && after.length === 0
        ? to + hold
        : ringsUntil([...strikes, ...after], to, target, secondsPerBeat);
    const landFor = Math.min(hold + TAIL, ringsTo - to, nextFigure - to);
    const full = Math.min(hold / 2, landFor / 2);
    const fade = { time: to + full, value: FADE_TO, ramp: landFor - full, curve: EXPONENTIAL };
    const landing = { midi: target, time: to, duration: landFor, velocity: velocity * LANDING, levels: [fade] };
    const heldFrom = Math.max(from, current.time);
    // The figures take turns, and a part swells into a louder one
    const figure = bar === bars - 1 && rises ? SWELL : FIGURES[(index + variation) % FIGURES.length];

    if (figure === SWELL && to - heldFrom >= secondsPerBar / 2 - EPSILON) {
      const held = nearest(pitchesOf(toneClasses(current.chord)), target);
      // Grown from soft, starting a moment after the gain is set so the two don't land together
      const grow = [
        { time: heldFrom, value: SWELL_FROM, ramp: 0 },
        { time: heldFrom + 0.01, value: 1, ramp: to - heldFrom - 0.01, curve: EXPONENTIAL },
      ];

      if (held === target) {
        notes.push({
          ...landing,
          time: heldFrom,
          duration: to - heldFrom + landFor,
          velocity: velocity * HELD,
          levels: [...grow, fade],
        });
      } else if (held !== null) {
        notes.push(
          { midi: held, time: heldFrom, duration: to - heldFrom, velocity: velocity * HELD, levels: grow },
          landing,
        );
      }

      return;
    }

    // A swell with no room for its held note, or a figure out of range, answers instead. An answer
    // runs up or down by the coin.
    const shape = {
      to,
      unit,
      chordAt: (time) => sounding(time)?.chord || current.chord,
      down: coin(bar + variation * 7, target),
    };
    const run = (leadIns[figure] && leadIns[figure](target, shape)) || leadIns[ANSWER](target, shape);

    if (!run) return;

    run.forEach(({ time, midi, hold = unit, level }, i) =>
      notes.push({
        midi,
        time,
        duration: hold * RUN_SHARE,
        velocity: velocity * (level ?? RUN_FROM + RUN_RISE * i),
        // Back to full for the run, after the last figure's fade
        ...(i === 0 ? { levels: [{ time, value: 1, ramp: 0 }] } : null),
      }),
    );
    notes.push(landing);
  });

  return notes;
};
